import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../services/auth/auth_service';

/** Firebase error codes meaning the session is no longer usable, so the user must sign in again. */
const SESSION_ENDED_CODES = new Set([
  'auth/user-disabled',
  'auth/user-token-expired',
  'auth/user-not-found',
  'auth/invalid-user-token',
  'auth/null-user',
]);

function errorCode(err: unknown): string | undefined {
  return typeof err === 'object' && err !== null && 'code' in err && typeof err.code === 'string'
    ? err.code
    : undefined;
}

/**
 * Core email verification logic. Accepts injected services so it can be called
 * from contexts without injection (e.g. RxJS callbacks).
 *
 * Never rejects: a thrown guard fails the navigation, and on a cold load that
 * leaves the user staring at a blank page.
 */
export async function checkEmailVerified(
  authService: AuthenticationService,
  router: Router,
  state: RouterStateSnapshot
): Promise<boolean> {
  const app = environment.app;

  // Only enforce email verification for trials app
  if (app !== 'trials' && app !== 'trialsClinic') {
    return true;
  }

  // Firebase has already resolved auth state (app initialiser), so no user here means the
  // loggedIn cookie is stale — e.g. Firebase cleared an invalidated user on startup.
  if (!authService.getUID()) {
    authService.clearLocalSession();
    router.navigate(['/login'], { queryParams: { redirectTo: state.url } });
    return false;
  }

  let isVerified: boolean;
  try {
    // Reload token and check verification status
    isVerified = await authService.reloadAndCheckVerification();
  } catch (err) {
    if (SESSION_ENDED_CODES.has(errorCode(err) ?? '')) {
      await authService.logout().catch(() => {});
      router.navigate(['/login'], { queryParams: { redirectTo: state.url } });
      return false;
    }

    // Transient failure (e.g. auth/network-request-failed) — fall back to the
    // last known verification status rather than failing the navigation.
    isVerified = authService.isVerified();
  }

  if (!isVerified) {
    router.navigate(['/verify-email'], {
      queryParams: { redirectTo: state.url },
      queryParamsHandling: 'merge',
    });
    return false;
  }

  return true;
}

/**
 * Guard that checks if the user's email is verified.
 * Only active for trials app - redirects unverified users to verify-email page.
 * For other apps, it always allows access.
 */
export const EmailVerifiedGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  return checkEmailVerified(authService, router, state);
};
